import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { useI18N } from '@tm/client-form/src/hooks/useI18n'
import { useOtherOptions } from '@tm/client-form/src/hooks/useOtherOptions'
import { DropdownField, MultidropdownField } from '@tm/shared-lib/src/field'
import { useCallback, useState } from 'react'
import { arrayValue, InputProps, stringValue } from './types'

export function DropdownInput(props: InputProps<DropdownField | MultidropdownField>) {
  const { tt, t } = useI18N()
  const { field, value, onChange, isSubmitting } = props
  const isMultiple = field.type === 'multidropdown'
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const { otherOptionValues, otherOptionLabels, setOptionValue, otherOptionByOptionId } = useOtherOptions(
    field.name,
    field.options
  )
  const otherOptions = isMultiple
    ? arrayValue(value).filter(value => otherOptionByOptionId[value])
    : [stringValue(value)].filter(value => otherOptionByOptionId[value])

  const label = tt(field.label)

  const closeSelect = useCallback(() => {
    setMenuIsOpen(false)
  }, [])

  return (
    <>
      <TextField
        value={isMultiple ? arrayValue(value) : stringValue(value, '')}
        onChange={event => {
          onChange(event.target.value)
        }}
        disabled={isSubmitting}
        fullWidth
        variant="filled"
        key={`f-${field.name}`}
        hiddenLabel={!label}
        label={label}
        SelectProps={{
          multiple: field.type === 'multidropdown',
          open: menuIsOpen,
          onOpen: () => setMenuIsOpen(true),
          onClose: () => setMenuIsOpen(false),
          MenuProps: isMultiple
            ? {
                PaperProps: {
                  sx: {
                    maxHeight: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                  },
                },
                MenuListProps: {
                  sx: {
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: 'calc(300px - 48px)', // Space for button height
                    paddingBottom: 0,
                  },
                },
              }
            : undefined,
        }}
        select>
        {field.options?.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {tt(option.label)}
          </MenuItem>
        ))}
        {isMultiple ? (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              padding: 1,
              margin: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}>
            <Button onClick={closeSelect} size="medium" fullWidth>
              {t('common.done')}
            </Button>
          </Box>
        ) : null}
      </TextField>
      {otherOptions.map(optionId => (
        <TextField
          value={otherOptionValues[optionId]}
          onChange={event => {
            setOptionValue(event.target.value, optionId)
          }}
          label={tt(otherOptionLabels[optionId])}
          disabled={isSubmitting}
          fullWidth
          variant="filled"
          key={`f-${field.name}-${optionId}`}
        />
      ))}
    </>
  )
}
